import React, { useState } from 'react';
import { Share2, Copy, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

/**
 * ShareButton — uses the Web Share API on mobile, falls back to copy-to-clipboard
 * on desktop. Place at the top of any user-facing detail page (TrackOrder, VendorMenu).
 */
const ShareButton = ({ url, title, text, variant = 'pill' }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const fullUrl = url || (typeof window !== 'undefined' ? window.location.href : '');

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ url: fullUrl, title: title || document.title, text });
        return;
      }
    } catch (e) { /* user cancelled */ }
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success(t('common.copied', 'Link copied'));
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.error('Could not copy link');
    }
  };

  const base = 'inline-flex items-center gap-1.5 text-sm font-medium transition-colors';
  const styles = variant === 'pill'
    ? 'h-9 px-3 rounded-full border border-slate-200 bg-white text-slate-700 hover:border-slate-300'
    : variant === 'ghost'
    ? 'h-9 px-2 text-slate-600 hover:text-slate-900'
    : 'h-9 px-3 rounded-lg bg-slate-900 text-white hover:bg-slate-800';

  return (
    <button onClick={share} className={`${base} ${styles}`} data-testid="share-button" aria-label={t('common.share', 'Share')}>
      {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Share2 className="h-4 w-4" />}
      <span>{t('common.share', 'Share')}</span>
    </button>
  );
};

export default ShareButton;
